import React, { useMemo } from 'react';
import { Card, Empty, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import '../student/AIPlayGroundLessonPlayer.css';
import { TAIPlayGroundDatasetEntry, TAIPlayGroundLessonConfig } from '../../../models/courses/aiPlayGround';
import { getImagePath, getSoundPath } from '../../../utils/format/valueFormat';


type LanguageKey = 'fr' | 'en' | 'ar';

interface AIPlayGroundLessonTeacherProps {
  config: TAIPlayGroundLessonConfig;
  statement?: string;
}

const getLanguageKey = (language: string): LanguageKey => {
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('ar')) return 'ar';
  return 'en';
};

const getFeaturesByLanguage = (entry: TAIPlayGroundDatasetEntry, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return entry.features_fr ?? entry.features_en ?? entry.features_ar ?? [];
    case 'ar':
      return entry.features_ar ?? entry.features_en ?? entry.features_fr ?? [];
    default:
      return entry.features_en ?? entry.features_fr ?? entry.features_ar ?? [];
  }
};

const AIPlayGroundLessonTeacher: React.FC<AIPlayGroundLessonTeacherProps> = ({ config, statement }) => {
  const { t, i18n } = useTranslation();
  const language = getLanguageKey(i18n.language || 'en');

  const entries = useMemo(() => Object.entries(config.dataset || {}), [config.dataset]);
  const noise = useMemo(() => {
    switch (language) {
      case 'fr':
        return config.bruit_fr ?? config.bruit_en ?? config.bruit_ar ?? [];
      case 'ar':
        return config.bruit_ar ?? config.bruit_en ?? config.bruit_fr ?? [];
      default:
        return config.bruit_en ?? config.bruit_fr ?? config.bruit_ar ?? [];
    }
  }, [config, language]);

  if (!entries.length) {
    return <Empty description={t('aiPlayGround.student.empty')} />;
  }

  return (
    <div className="aimb-player">
      {statement || config.statement ? (
        <div className="aimb-statement">{statement || config.statement}</div>
      ) : null}

      <div className="aimb-keys-grid">
        {entries.map(([keyId, entry]) => {
          const features = getFeaturesByLanguage(entry, language);
          const keyLabel = entry.key || keyId;
          const isImage = /\.(png|jpe?g|gif|svg|webp)$/i.test(keyLabel) || keyLabel.includes('/images/');
          const isAudio = /\.(mp3|wav|ogg|m4a)$/i.test(keyLabel) || keyLabel.includes('/sounds/') || keyLabel.includes('/audio/');
          return (
            <Card key={keyId} title={!isImage && !isAudio ? keyLabel : undefined} className="aimb-dropzone">
              {isImage && (
                <div className="ai-dataset-key-media" style={{ marginBottom: 10 }}>
                  <img
                    src={getImagePath(keyLabel)}
                    alt="key"
                    draggable={false}
                    style={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 12 }}
                  />
                </div>
              )}
              {isAudio && (
                <div className="ai-dataset-key-media ai-dataset-key-media--audio" style={{ marginBottom: 10 }}>
                  <audio src={getSoundPath(keyLabel)} controls draggable={false} onPointerDown={(e) => e.stopPropagation()} />
                </div>
              )}
              {features.length === 0 ? (
                <Empty description={t('aiPlayGround.student.emptyFeatures')} />
              ) : (
                <Space wrap>
                  {features.map((f, idx) => {
                    const fIsImage = /\.(png|jpe?g|gif|svg|webp)$/i.test(f) || f.includes('/images/');
                    const fIsAudio = /\.(mp3|wav|ogg|m4a)$/i.test(f) || f.includes('/sounds/') || f.includes('/audio/');
                    if (fIsImage) {
                      return (
                        <span key={`${keyId}-f-${idx}`}>
                          <img
                            src={getImagePath(f)}
                            alt="feature"
                            style={{ maxHeight: 60, maxWidth: 120, objectFit: 'cover', borderRadius: 6 }}
                          />
                        </span>
                      );
                    }
                    if (fIsAudio) {
                      return (
                        <audio
                          key={`${keyId}-f-${idx}`}
                          src={getSoundPath(f)}
                          controls
                          style={{ height: 40 }}
                          onPointerDown={(e) => e.stopPropagation()}
                        />
                      );
                    }
                    return <Tag key={`${keyId}-f-${idx}`}>{f}</Tag>;
                  })}
                </Space>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="aimb-section-card" title={t('aiPlayGround.teacher.noiseTitle')}>
        {noise.length === 0 ? (
          <Empty description={t('aiPlayGround.teacher.emptyNoise')} />
        ) : (
          <Space wrap>
            {noise.map((n, idx) => {
              const nIsImage = /\.(png|jpe?g|gif|svg|webp)$/i.test(n) || n.includes('/images/');
              const nIsAudio = /\.(mp3|wav|ogg|m4a)$/i.test(n) || n.includes('/sounds/') || n.includes('/audio/');
              if (nIsImage) {
                return (
                  <span key={`noise-${idx}`}>
                    <img
                      src={getImagePath(n)}
                      alt="noise"
                      style={{ maxHeight: 60, maxWidth: 120, objectFit: 'cover', borderRadius: 6 }}
                    />
                  </span>
                );
              }
              if (nIsAudio) {
                return (
                  <audio
                    key={`noise-${idx}`}
                    src={getSoundPath(n)}
                    controls
                    style={{ height: 40 }}
                    onPointerDown={(e) => e.stopPropagation()}
                  />
                );
              }
              return <Tag key={`noise-${idx}`}>{n}</Tag>;
            })}
          </Space>
        )}
      </Card>

      <Typography.Paragraph type="secondary">
        {t('aiPlayGround.teacher.description')}
      </Typography.Paragraph>
    </div>
  );
};

export default AIPlayGroundLessonTeacher;
